import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import {
  CloseCircleOutlined,
  BoxIcons,
  IconTypes,
  UserOutlined,
  ExclamationCircleFilled,
} from "us.icons";
import {
  changeLanguage,
  navigateStart,
  userManagementActions,
} from "us.common/actions";
import {
  $Layout,
  $Dropdown,
  $Badge,
  MegaMenu,
  SearchBar,
  NotificationMenu,
  $Avatar,
  $Typography,
  $Popover,
  $Button,
} from "us.common/components";
import { MenuButtonStyle, MenuStyle } from "./Constants";
import { useMsal } from "@azure/msal-react";
import { BrowserUtils } from "@azure/msal-browser";
import { i18n } from "us.helper";
import "./AppLayout.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { IAppLayoutProps, ILanguageChangePopover } from "./Interfaces";
import * as Azure from "us.helper/azure";
import { getInitialSelectedKey } from "./Functions";
import { findRegionalSetting } from "us.common/functions";
import { isArray } from "lodash";

const { user, preferredLanguage } = userManagementActions;

const { Content } = $Layout;

const { Text } = $Typography;

/**
 * @description - Main application layout.
 * @author Samitha Hewawasam <samitha@unicorn-solutions.com>
 * @since 23/05/2022
 * */
const AppLayout: React.FC<PropsFromRedux & IAppLayoutProps> = ({
  currentLanguage,
  children,
  headerExtra,
  userProfilePhoto,
  tenant,
  megaMenu = true,
  enableSearch = true,
  enableSettings = false,
  headerSettings,
  changeLanguage,
  authorizationProfile,
  updatePreferredLanguage,
}) => {
  const { t } = useTranslation(["US.COLLECTION.COMMON"], {
    useSuspense: true,
  });

  const auth = new Azure.ADAuth();
  const currentUser: any = auth.currentUser();

  const { instance } = useMsal();
  const navigator = useNavigate();
  const { state } = useLocation();

  const [isMenuOpen, setMenuOpen] = useState<boolean>(false);
  const [ellipsis] = useState(true);
  const [LanguagePopover, setLanguagePopover] =
    useState<ILanguageChangePopover>({
      selectedLanguage: currentLanguage,
      selectedKey: getInitialSelectedKey(
        authorizationProfile?.regionalSettings,
        currentLanguage
      ),
      visibility: false,
    });

  // Ref to mega menu container; initialize with null to allow proper null checks
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (megaMenu) {
      // add when mounted
      document.addEventListener("mousedown", onClickMenuOutSide);

      // return function to be called when unmounted
      return () => {
        document.removeEventListener("mousedown", onClickMenuOutSide);
      };
    }
  }, []);

  /**
   * @function
   * @description - Function for handle user logout.
   */
  const handleLogout = () => {
    // remove saved quick links
    localStorage.removeItem("@quickLinks");
    instance.logoutRedirect({
      account: instance.getActiveAccount(),
      onRedirectNavigate: () => !BrowserUtils.isInIframe(),
    });
  };

  /**
   * @function
   * @description - If the click is outside the menu, close the menu.
   * @param e - this is the event object that is passed to the function.
   */
  const onClickMenuOutSide = (e: any) => {
    // If ref not yet bound (e.g., during first render or megaMenu disabled) do nothing
    if (!menuRef.current) return;
    if (menuRef.current.contains(e.target)) {
      // inside click
      return;
    }
    // outside click
    setMenuOpen(false);
  };

  /**
   * @function
   * @description - Menu and header navigation's.
   * @param {string} url - navigation URL.
   */
  const navigate = (url: string, locationState?: any) => {
    navigator(
      url,
      locationState && Object.keys(locationState).length > 0
        ? { state: locationState }
        : { state }
    );
    setMenuOpen(false);
  };

  /**
   * @function
   * @description - Change regional settings.
   * @param languageCode - language code for selected language
   */
  const changeRegionalSettings = (languageCode: any) => {
    const { regionalSettings } = authorizationProfile ?? {};
    try {
      const selectedRegion = findRegionalSetting(
        regionalSettings,
        (item) => item.languageCode.toLowerCase() === languageCode.toLowerCase()
      );
      if (selectedRegion) {
        i18n.changeLanguage(selectedRegion.languageCode);
        changeLanguage(
          selectedRegion.languageCode,
          selectedRegion.currency,
          selectedRegion.systemDateFormat,
          selectedRegion.thousandSeparator,
          selectedRegion.decimalSepearator
        );
        updatePreferredLanguage &&
          updatePreferredLanguage({
            userId: currentUser?.oid,
            languageCode: selectedRegion?.languageCode,
          });
      } else {
        const defaultRegion = findRegionalSetting(
          regionalSettings,
          (item) => item.isDefaultRegion
        );
        if (defaultRegion) {
          i18n.changeLanguage(defaultRegion.languageCode);
          changeLanguage(
            defaultRegion.languageCode,
            defaultRegion.currency,
            defaultRegion.systemDateFormat,
            defaultRegion.thousandSeparator,
            defaultRegion.decimalSepearator
          );
          updatePreferredLanguage &&
            updatePreferredLanguage({
              userId: currentUser?.oid,
              languageCode: defaultRegion?.languageCode,
            });
        }
      }
    } catch (error) {
      const defaultRegion = findRegionalSetting(
        regionalSettings,
        (item) => item.isDefaultRegion
      );
      if (defaultRegion) {
        i18n.changeLanguage(defaultRegion.languageCode);
        changeLanguage(
          defaultRegion.languageCode,
          defaultRegion.currency,
          defaultRegion.systemDateFormat,
          defaultRegion.thousandSeparator,
          defaultRegion.decimalSepearator
        );
        updatePreferredLanguage &&
          updatePreferredLanguage({
            userId: currentUser?.oid,
            languageCode: defaultRegion?.languageCode,
          });
      }
    }
  };

  /**
   * @function
   * @param code - regional code to get details
   * @description - Get regional details by code.
   * @returns
   */
  const getRegionalDetails = (code: string) => {
    try {
      return findRegionalSetting(
        authorizationProfile?.regionalSettings,
        (item) => item.id == code
      );
    } catch (error) {
      return findRegionalSetting(
        authorizationProfile?.regionalSettings,
        (item) => item.isDefaultRegion
      );
    }
  };

  /**
   * @function
   * @description handle language confirm popover
   */
  const languagePopoverHandler = (key: string) => {
    const selectedRegion = getRegionalDetails(key);
    if (selectedRegion) {
      setLanguagePopover({
        selectedLanguage: selectedRegion?.languageCode,
        selectedKey: key,
        visibility: true,
      });
    }
  };

  const languageMenuNew = useMemo(() => {
    let menu: any = { items: [] };
    try {
      if (isArray(authorizationProfile?.regionalSettings)) {
        authorizationProfile?.regionalSettings.forEach((item: any) => {
          menu.items.push({
            key: item?.id,
            label: item?.languageName,
            onClick: (data: any) => languagePopoverHandler(data.key),
          });
        });
      }
      return menu;
    } catch (error) {
      return menu;
    }
  }, [authorizationProfile?.regionalSettings, LanguagePopover.selectedKey]);

  const profileMenu = {
    items: [
      {
        key: "profile",
        label: (
          <div className="d-flex align-items-center">
            <div className="mr-2">
              <$Avatar
                src={userProfilePhoto}
                size={48}
                icon={<UserOutlined />}
              />
            </div>
            <div className="d-flex flex-column profile-info">
              <Text
                className="font-weight-bold"
                ellipsis={ellipsis ? { tooltip: {} } : false}
              >
                {instance.getActiveAccount()?.name ||
                  t("US.COLLECTION.COMMON:COMMON.RECEPTIONIST")}
              </Text>
              <Text
                className="text-muted"
                style={{ fontSize: "0.7rem" }}
                ellipsis={
                  ellipsis
                    ? {
                        tooltip: `${instance.getActiveAccount()?.username}`,
                      }
                    : false
                }
              >
                {instance.getActiveAccount()?.username}
              </Text>
            </div>
          </div>
        ),
      },
      {
        key: "divider",
        type: "divider",
      },
      {
        key: "logout",
        label: t("US.COLLECTION.COMMON:COMMON.SIGN_OUT"),
        onClick: handleLogout,
      },
    ],
  };

  return (
    <>
      <$Layout className="layout">
        <div className="us-header">
          <div className="logo" onClick={() => navigate("/")}>
            <$Badge
              count={`v${sessionStorage.getItem("productVersion")}`}
              offset={[8, 14]}
              className="header-badge"
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect fill="#052832" width="48" height="48" />
                <g>
                  <path id="Path_1438" fill="#1db04c" d="M41.96,24c0,9.92-8.03,17.96-17.95,17.97-9.92,0-17.96-8.03-17.97-17.95,0-9.92,8.03-17.96,17.95-17.97,4.22,0,8.31,1.49,11.55,4.2l-1.53,2.23c-6.36-5.5-15.97-4.81-21.47,1.55-5.5,6.36-4.81,15.97,1.55,21.47,6.36,5.5,15.97,4.81,21.47-1.55,4.69-5.42,4.97-13.39.65-19.12l1.58-2.34c2.7,3.23,4.18,7.3,4.17,11.51M16.5,21.74c.38-.38.99-.38,1.37,0l5.79,5.79,9.17-13.34c-5.41-4.87-13.75-4.42-18.61.99-4.87,5.41-4.42,13.75.99,18.61,5.41,4.87,13.75,4.42,18.61-.99,4.03-4.48,4.5-11.13,1.15-16.14-3.95,5.85-9.62,14.26-10.11,15.02-.26.52-.89.74-1.41.48-.11-.06-.22-.13-.3-.22l-.93-.92s-.08-.06-.12-.1l-6.71-6.71c-.38-.38-.38-.99,0-1.37l1.11-1.11Z"/>
                </g>
              </svg>
            </$Badge>
          </div>
          {megaMenu && (
            <div className="us-mega-menu" ref={menuRef}>
              <div
                className={
                  isMenuOpen
                    ? MenuButtonStyle.SELECTED
                    : MenuButtonStyle.UN_SELECTED
                }
                onClick={() => setMenuOpen(!isMenuOpen)}
                id="megaMenuBtn"
              >
                <span className="mr-1">
                  {t("US.COLLECTION.COMMON:COMMON.MENU")}
                </span>
                <CloseCircleOutlined
                  className="mega-menu-close"
                  type="close-circle"
                />
              </div>
              <MegaMenu
                style={isMenuOpen ? MenuStyle.OPEN : MenuStyle.CLOSE}
                isMenuOpen={isMenuOpen}
                onNavigate={navigate}
              />
            </div>
          )}
          {enableSettings && headerSettings}
          {enableSearch && (
            <div className="center-search" onClick={() => setMenuOpen(false)}>
              <div className="d-flex align-items-center justify-content-end auto-complete-con">
                <SearchBar />
              </div>
            </div>
          )}
          {!enableSearch && !headerExtra && <div className="center-search" />}
          {headerExtra}
          <div className="header-options flex-column align-items-start tenant-wrap">
            <Text
              ellipsis={
                ellipsis
                  ? {
                      tooltip: `${tenant?.displayName}`,
                    }
                  : false
              }
            >
              {tenant?.displayName}{" "}
            </Text>
          </div>

          <div className="d-flex mr-3">
            <NotificationMenu />

            <$Popover
              placement="bottomRight"
              content={
                <div style={{ maxWidth: "300px" }}>
                  <div className="ant-popover-message align-items-start">
                    <ExclamationCircleFilled className="mt-1 mr-2 text-warning" />
                    <div className="ant-popover-message-title">
                      <div
                        // style={{ paddingBottom: "0.3rem" }}
                        data-testid={"language-change-conformation"}
                      >
                        {/* Are you sure, You want to change the language? */}
                        {t(
                          "US.COLLECTION.COMMON:COMMON.DO_YOU_WANT_TO_CHANGE_THE_LANGUAGE?"
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="ant-popover-buttons">
                    <$Button
                      size="small"
                      onClick={() => {
                        setLanguagePopover({
                          ...LanguagePopover,
                          visibility: false,
                        });
                      }}
                      data-testid={"language-change-conformation-no"}
                    >
                      {t("US.COLLECTION.COMMON:COMMON.NO")}
                    </$Button>
                    <$Button
                      size="small"
                      type="primary"
                      onClick={() => {
                        changeRegionalSettings(
                          LanguagePopover.selectedLanguage
                        );
                        setLanguagePopover({
                          ...LanguagePopover,
                          visibility: false,
                        });
                      }}
                      data-testid={"language-change-conformation-yes"}
                    >
                      {t("US.COLLECTION.COMMON:COMMON.YES")}
                    </$Button>
                  </div>
                </div>
              }
              trigger="click"
              open={LanguagePopover.visibility}
            >
              <$Dropdown
                menu={languageMenuNew}
                className="d-flex flex-row search-by-con"
              >
                <a
                  className="d-flex align-items-center pt-1 ant-dropdown-link px-2"
                  onClick={(e) => e.preventDefault()}
                >
                  <$Badge
                    count={currentLanguage.toUpperCase()}
                    className="header-badge"
                  >
                    <BoxIcons
                      className="icon-btn mb-1"
                      type={IconTypes.BOX_ICON}
                      name="language"
                    />
                  </$Badge>
                </a>
              </$Dropdown>
            </$Popover>
          </div>
          <div className="header-options">
            <$Dropdown
              trigger={["click"]}
              menu={profileMenu}
              overlayClassName="profile-dropdown"
            >
              <a
                className="d-flex align-items-center ant-dropdown-link px-1"
                onClick={(e) => e.preventDefault()}
              >
                <$Avatar
                  src={userProfilePhoto}
                  icon={<UserOutlined />}
                />
              </a>
            </$Dropdown>
          </div>
        </div>
        <Content>
          <div className="content">{children}</div>
          <div id="hiddenOverlay"></div>
        </Content>
      </$Layout>
    </>
  );
};

const mapStateToProps = (state: any) => {
  const { common, UserInformation, AuthorizationProfile } = state;
  return {
    currentLanguage: common.currentLanguage,
    userProfilePhoto: UserInformation.userProfilePhoto,
    tenant: UserInformation.tenant,
    authorizationProfile: AuthorizationProfile.authorizationProfile,
  };
};

const mapDispatchToProps = {
  navigateStart,
  updateUser: user.update,
  updatePreferredLanguage: preferredLanguage.save,
  changeLanguage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AppLayout);
